// Floor quantity ENTRY (spec 04 §4 row 10 — "qty stepper + numpad", D-091).
//
// The pure half of the S10 quantity control, kept out of the component so the
// arithmetic and the wire discipline are exhaustively unit-testable:
//
//  * THE WIRE IS CANONICAL DOT, ALWAYS. Every function here takes and returns
//    the contract's exact-decimal string (`ProductionQty` — up to 11 integer
//    digits, up to 3 fraction digits, "." separator). Locale is DISPLAY ONLY:
//    ru shows a comma, the payload never does. `displayQty`/`parseTypedQty`
//    are the one boundary where the two meet.
//
//  * PRECISION IS THE UNIT'S, NEVER A HARDCODED 3. The catalogue models
//    `decimal_precision` per uom (spec 01 §2.1, 0..6); the snapshot serves it
//    per op/material line clamped to the §2.4 wire ceiling of 3
//    (`uom_decimal_precision`). Precision 0 is an INTEGER unit: the numpad
//    offers no separator key and typed separators are refused.
//
//  * ±1 STAYS EXACT ON A DECIMAL. `bumpQty` does integer arithmetic on the
//    integer part and carries the fraction VERBATIM — float arithmetic on
//    "0.1"-style fractions is precisely what an exact-decimal wire exists to
//    avoid.

/**
 * The §2.4 wire ceiling: `numeric(14,3)` / `ProductionQty`'s regex — 11
 * integer digits, 3 fraction digits. Mirrored, not imported: the contract
 * package publishes schemas, and the one number is asserted against the
 * generated schema in this file's test.
 */
export const QTY_MAX_INT_DIGITS = 11;
export const QTY_MAX_PRECISION = 3;

/**
 * MODULE 06'S OWN SCALE — `numeric(18,6)` (D-156) — and the widest fraction
 * this control will ever admit, whatever a caller asks for.
 *
 * THE FIELD'S WIRE IS NOT ALWAYS §2.4'S (2026-08-21 review). This control was
 * written for production quantities, whose wire is `numeric(14,3)`, and the
 * 3-dp ceiling was hardcoded into the TYPED path. Then the guided cut's
 * handles pointed it at a MEASURE — seeded from module 06's six places, e.g.
 * `1543.500250`. Focus selects all, so the first keystroke replaced the whole
 * value and worked; the SECOND tap on a touch screen places a caret and drops
 * the selection, and from there every keystroke was refused, because backspace
 * yields `1543.50025` — five fraction digits — and a 3-dp regex says no. The
 * field goes dead mid-edit and the operator concludes the screen is broken.
 *
 * So the ceiling is now the FIELD'S, passed by the caller, and this is the
 * absolute bound on what any caller may ask for: six places is the widest
 * exact-decimal grain on any wire this control is pointed at, and a wider one
 * would put text in the field that `parseMeasure` cannot read back.
 */
export const MEASURE_MAX_PRECISION = 6;

/**
 * The FIELD's wire ceiling — how many fraction digits the payload behind this
 * control can carry. Defaults to §2.4's 3 when a caller says nothing, which is
 * every caller but the guided cut's handles.
 */
export function clampWirePrecision(raw: unknown): number {
  if (typeof raw !== "number" || !Number.isInteger(raw)) return QTY_MAX_PRECISION;
  return Math.max(0, Math.min(MEASURE_MAX_PRECISION, raw));
}

/**
 * The stale-cache guard: an e15 snapshot cached before the D-091 amendment
 * has no `uom_decimal_precision`, and a malformed one is not a number this
 * control should trust. Anything that is not an integer 0..`wire` falls back
 * to `wire` — decimals are the NORMAL case for cable lengths, and an
 * integer-only fallback would refuse a real measured 49.4 m.
 *
 * `wire` is the FIELD's ceiling (above) and defaults to §2.4's 3, so
 * `clampPrecision(x)` — every call site but the guided cut — is byte-for-byte
 * the function it has always been.
 */
export function clampPrecision(raw: unknown, wire: unknown = QTY_MAX_PRECISION): number {
  const ceiling = clampWirePrecision(wire);
  if (typeof raw !== "number" || !Number.isInteger(raw)) return ceiling;
  return Math.max(0, Math.min(ceiling, raw));
}

/**
 * The locale's decimal separator, for DISPLAY and for the numpad key's label
 * (ru: "," — the separator a KazKabel operator learned in school). The wire
 * never sees it.
 */
export function decimalSeparator(locale: string): string {
  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(1.1);
    return parts.find((p) => p.type === "decimal")?.value ?? ".";
  } catch {
    return ".";
  }
}

/** Canonical editing value → what the input SHOWS (locale separator). */
export function displayQty(value: string, separator: string): string {
  return separator === "." ? value : value.replace(".", separator);
}

/**
 * A keystroke-typed value → the canonical editing value, or undefined when
 * the keystroke must be refused (the input then keeps its previous value).
 * Accepts BOTH "." and "," while typing — a hardware keyboard's numeric pad
 * has a dot, a ru layout has a comma, and refusing either would make the
 * control locale-hostile in exactly the way the display localisation exists
 * to avoid. A trailing separator is legal mid-entry ("49." on the way to
 * "49.4"); `normalizeQty` strips it before anything reaches a builder.
 */
export function parseTypedQty(raw: string, precision: number): string | undefined {
  const canonical = raw.replace(",", ".");
  const re =
    precision > 0
      ? new RegExp(`^\\d{0,${String(QTY_MAX_INT_DIGITS)}}(\\.\\d{0,${String(precision)}})?$`)
      : new RegExp(`^\\d{0,${String(QTY_MAX_INT_DIGITS)}}$`);
  return re.test(canonical) ? canonical : undefined;
}

/**
 * ±1, EXACT, fraction carried verbatim — the one-tap integer step the spec's
 * stepper half is (integer steps must remain one-tap; fractional entry is the
 * numpad's job). Floor at zero: a −1 that would go below zero lands on "0",
 * fraction and all — the same clamp the integer-only stepper always had.
 */
export function bumpQty(value: string, delta: 1 | -1): string {
  const canonical = value.replace(",", ".").replace(/\.$/, "");
  const [intPart = "0", fracPart] = canonical.split(".");
  const current = intPart.length === 0 ? 0 : Number(intPart);
  if (!Number.isFinite(current)) return value;
  const frac = fracPart !== undefined && fracPart.length > 0 ? fracPart : undefined;
  if (delta < 0 && current === 0) return "0";
  const nextInt = current + delta;
  if (String(nextInt).length > QTY_MAX_INT_DIGITS) return value;
  return frac === undefined ? String(nextInt) : `${String(nextInt)}.${frac}`;
}

/**
 * One numpad digit tap. `fresh` is calculator entry semantics: the first key
 * after the pad opens REPLACES the value (the same affordance the input's
 * select-on-focus gives a keyboard), every later key appends. Appends beyond
 * the unit's precision or the wire's 11 integer digits are ignored — the tap
 * simply does nothing, which on a phone in gloves beats an error toast.
 */
export function numpadDigit(
  value: string,
  digit: string,
  precision: number,
  fresh: boolean,
): string {
  if (!/^\d$/.test(digit)) return value;
  const base = fresh ? "" : value;
  const [intPart = "", fracPart] = base.split(".");
  if (base.includes(".")) {
    if ((fracPart ?? "").length >= precision) return base === "" ? "0" : base;
    return `${intPart}.${fracPart ?? ""}${digit}`;
  }
  if (intPart.length >= QTY_MAX_INT_DIGITS) return base;
  // "0" + digit → the digit (no leading zeros; "0.x" arrives via the
  // separator key, not by typing past a zero).
  const nextInt = intPart === "0" ? digit : `${intPart}${digit}`;
  return nextInt;
}

/**
 * The separator key. Only offered when the unit has fraction digits at all;
 * defensive no-op otherwise. On a fresh entry (or an empty value) it starts
 * "0." — the digit-less way a worker keys ".4".
 */
export function numpadSeparator(value: string, precision: number, fresh: boolean): string {
  if (precision <= 0) return value;
  const base = fresh ? "" : value;
  if (base.includes(".")) return base;
  return base === "" ? "0." : `${base}.`;
}

/** Backspace: last character off; an emptied value reads "0". */
export function numpadBackspace(value: string): string {
  const shorter = value.slice(0, -1);
  return shorter.length === 0 ? "0" : shorter;
}

/**
 * "12." → "12", "" → "0", ".4" → "0.4" — the wire regex (`ProductionQty`)
 * admits none of the left-hand forms. (Moved here from op-run.tsx with
 * D-091; the ".4" case is NEW with it — the typed path always admitted a
 * bare leading separator mid-entry, and before decimals were the headline
 * nobody typed one. Without the prefix the payload dies on the contract
 * regex as a 400 the operator cannot read.)
 */
export function normalizeQty(raw: string): string {
  const cleaned = raw.replace(/\.$/, "");
  if (cleaned.length === 0) return "0";
  return cleaned.startsWith(".") ? `0${cleaned}` : cleaned;
}
